<template>
  <div class="right-compare x-scrollbar">
    <div class="right-compare__header">
      <Segmented :items="items" v-model:model-value="preference" class="preference-segmented" />
    </div>
    <div class="right-compare__wrapper">
      <RightItem
        v-for="rights in displayRights"
        :key="rights.id + preference"
        class="right-compare__item"
        :equity="rights"
      />
      <RightTable sticky-top="0" />
      <Faq />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useConfig } from '@rendora/common/composables/useConfig';
import { Preference, usePayment } from '@rendora/common/features/payment/store';
import { getI18nService } from '@rendora/common/services/i18n';
import { IEquity, useRightsStore } from '@rendora/common/store/rights';
import { BUY_TYPE, IRightsType } from '@rendora/common/types/enums';
import Segmented, { type SegmentedItem } from '@rendora/design/components/segmented/Segmented.vue';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import Faq from './faq/index.vue';
import RightItem from './right-item/RightItem.vue';
import RightTable from './right-table/RightTable.vue';

const i18nService = getI18nService();
const $t = i18nService.t;
const rightsStore = useRightsStore();
const paymentStore = usePayment();
const { preference } = storeToRefs(paymentStore);
const { isUS } = useConfig();

const { rightsDisplay } = storeToRefs(rightsStore);

const checkIsCorrectCycle = (equity: IEquity, preference: Preference) => {
  return equity.rights_by_time.some(item => {
    if (item.buy_type !== BUY_TYPE.SUBSCRIBE) {
      return false;
    }

    switch (preference) {
      case Preference.MONTHLY:
        return item.days === 30;
      case Preference.YEARLY:
        return item.days === 365;
      default:
        return true;
    }
  });
};

const displayRights = computed(() => {
  return rightsDisplay.value?.rights.filter(r => {
    switch (preference.value) {
      case Preference.PERSONAL:
        return [IRightsType.TASTE, IRightsType.PERSONAL, IRightsType.PERSONAL_LITE].includes(
          r.rights_type,
        );
      case Preference.ENTERPRISE:
        // 24：企业版现在只能定制，所以收拢到 24 这个点，只用于展示，不参与逻辑
        return r.rights_type === IRightsType.ORG_CUSTOM;
      case Preference.MONTHLY:
        return checkIsCorrectCycle(r, preference.value);
      case Preference.YEARLY:
        return checkIsCorrectCycle(r, preference.value);
      default:
        return true;
    }
  });
});

const items: SegmentedItem[] = !isUS
  ? [
      {
        label: $t('ge-ren-ban'),
        key: Preference.PERSONAL,
      },
      {
        label: $t('qi-ye-ban'),
        key: Preference.ENTERPRISE,
      },
    ]
  : [
      {
        label: $t('yue-fu'),
        key: Preference.MONTHLY,
      },
      {
        label: $t('nian-fu'),
        key: Preference.YEARLY,
      },
    ];
</script>

<style lang="scss" scoped>
.right-compare {
  max-height: 80vh;
  overflow: auto;
}
.right-compare__header {
  margin: 16px auto 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  &:deep(.preference-segmented) {
    border-radius: 18px;
  }
  &:deep(.segmented__item) {
    display: flex;
    justify-content: center;
    white-space: nowrap;
    align-items: center;
    height: 32px;
    border-radius: 16px;
    white-space: nowrap;
  }
}

.right-compare__wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 0 12px 20px 20px;
  justify-content: center;
}

.right-compare__item {
  width: 310px;
}
</style>
